import { useEffect, useState } from "react";
import { useAccountingStore } from "../../../store/Accounting";
import { VerlustViewType } from "@/lib/supabase/supabaseTypes";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/dentlab/src/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/dentlab/src/components/ui/card";
import { ArrowUpRightFromSquare, Info } from "lucide-react";
import { IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";

const recipientType = (row: VerlustViewType) => {
	return row.recipient_client_id ? 'Kunde' : row.recipient_guarantor_id ? 'Garant' : 'Patient'
}

const formatCurrency = (amount?: number, currency = "CHF") => {
	if (amount === undefined || amount === null) return "--";
	return `${amount.toFixed(2)} ${currency}`;
};

const formatDate = (dateString?: string) => {
	if (!dateString) return "--";
	return new Date(dateString).toLocaleDateString('de-DE');
};

export const VerlustPage: React.FC = () => {
	const [loading, setLoading] = useState<boolean>(true);
	const navigate = useNavigate();

	const { verlustData, fetchVerlustData } = useAccountingStore((state) => ({
		verlustData: state.verlustData,
		fetchVerlustData: state.fetchVerlustData,
	}));

	useEffect(() => {
		const loadData = async () => {
			setLoading(true);
			await fetchVerlustData();
			setLoading(false);
		};

		loadData();
	}, [fetchVerlustData]);

	return (
		<div className="py-6">
			<div className="px-6 flex justify-center">
			<Card className="w-fit">
				<CardHeader>
					<div className="flex items-center gap-2">
						<TooltipWrapper title="Die Ansicht zeigt alle ausgebuchten Verluste je Empfänger.">
							<button type="button" className="text-gray-400 hover:text-gray-600 p-1" aria-label="Zweck anzeigen">
								<Info className="h-4 w-4" />
							</button>
						</TooltipWrapper>
						<CardTitle>Verlust-Übersicht</CardTitle>
					</div>
				</CardHeader>
				<CardContent>
					{loading ? (
						<div className="flex justify-center items-center py-8">
							<div className="text-gray-500">Lade Verlust-Daten...</div>
						</div>
					) : (
						<div className="flex justify-center">
							<Table className="w-auto">
								<TableHeader>
								<TableRow>
									<TableHead className="w-60">Empfänger</TableHead>
									<TableHead className="text-right w-32">Anzahl Rechnungen</TableHead>
									<TableHead className="text-right w-32">Gesamtverlust</TableHead>
									<TableHead className="text-right w-32">Durchschnittsverlust</TableHead>
									<TableHead className="text-right w-32">Letzter Verlust</TableHead>
									<TableHead className="w-20">Aktionen</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{verlustData.length === 0 ? (
									<TableRow>
										<TableCell colSpan={6} className="text-center py-8">
											<div className="text-gray-500">
												Keine Verlust-Einträge gefunden
											</div>
										</TableCell>
									</TableRow>
								) : (
									verlustData.map((verlust: VerlustViewType, index) => (
										<TableRow key={`${verlust.recipient_client_id || verlust.recipient_guarantor_id || verlust.recipient_patient_id}-${index}`} className="group hover:bg-gray-50 transition-colors">
											<TableCell className="font-medium">
												{verlust.recipient_name}
												{` (${recipientType(verlust)})`}
											</TableCell>
											<TableCell className="text-right font-mono">
												{verlust.invoice_count}
											</TableCell>
											<TableCell className="text-right font-mono text-red-600">
												{formatCurrency(Math.abs(verlust.total_loss_amount || 0))}
											</TableCell>
											<TableCell className="text-right font-mono text-red-600">
												{formatCurrency(Math.abs(verlust.average_loss_amount || 0))}
											</TableCell>
											<TableCell className="text-right">
												{formatDate(verlust.last_loss_date || undefined)}
											</TableCell>
											<TableCell>
												<div className="flex items-center justify-end w-full">
													{/* Fixed width container for consistent alignment */}
													<div className="flex items-center gap-1 min-w-[60px] justify-end">
														{/* Hover-visible primary actions */}
														<div className="flex gap-1 transition-opacity">
															<Tooltip title="Rechnungen anzeigen">
																<IconButton size="small" onClick={() => {
																	navigate(`/debitoren/fakturajournal?recipient=${
																		verlust.recipient_client_id ||
																		verlust.recipient_guarantor_id ||
																		verlust.recipient_patient_id
																	}`);
																}}>
																	<ArrowUpRightFromSquare className="h-4 w-4" />
																</IconButton>
															</Tooltip>
														</div>
													</div>
												</div>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
							</Table>
						</div>
					)}
				</CardContent>
			</Card>
			</div>
		</div>
	);
};
